import React from "react";
import Image from "next/image";
import { env } from "@/lib/environment";

import { Section } from "@/components/ui/Section";
import { Typography } from "@/components/ui";
import { LazyBackground } from "@/components/ui/LazyBackground";
import { TrackingId } from "@/lib/tracking";
import { GradientCardButtons } from "@/components/ui/GradientCardButtons.client";

export interface ButtonConfig {
  text: string;
  href?: string; // If not provided, uses Calendly
  variant?: "primary" | "secondary" | "outlinePrimary" | "outlineSecondary" | "ghost" | "accent" | "outlineAccent";
  target?: string;
  arrow?: "none" | "hover" | "grow";
  /** Optional custom classes applied to the rendered button */
  className?: string;
}

export interface StatItem {
  value: string;
  label: string;
}

interface GradientCardProps {
  title: string | React.ReactNode;
  className?: string;
  variant?: "default" | "cta-banner" | "one-row-cta-banner" | "stats-cta";
  subtitle?: string;
  centered?: boolean;
  buttonTrackingId?: {
    primaryId?: TrackingId;
    secondaryId?: TrackingId;
  };
  buttons?: ButtonConfig[];
  stats?: StatItem[];
  appImage?: boolean;
}
const imgBaseUrl = `${env.NEXT_PUBLIC_CDN_URL}/images/digital-signage-player/android`;

export const GradientCard: React.FC<GradientCardProps> = ({
  title,
  className = "mx-auto rounded-3xl shadow-[0px_3px_6px_rgba(0,0,0,.16)]",
  variant = "default",
  subtitle,
  centered = false,
  buttonTrackingId,
  buttons = [
    {
      text: "Start Free Trial",
      href: "https://console.pickcel.com/auth/register",
      variant: "accent",
    },
    { text: "Book Demo", variant: "outlineAccent" },
  ],
  stats,
  appImage = false,
}) => {
  const isCtaBanner = variant === "cta-banner";
  const isOneRowCtaBanner = variant === "one-row-cta-banner";
  const isStatsCta = variant === "stats-cta";

  if (isStatsCta) {
    return (
      <Section variant="default" background="white">
        <div className="mx-auto">
          <div className="bg-navy rounded-4xl p-8 md:p-10 xl:p-14 relative overflow-hidden">
            {/* Gradient background effects */}
            <Image
              src={`${env.NEXT_PUBLIC_CDN_URL}/images/shared/Gradient.ZIst2n15bYKk.png`}
              alt="gradient circle"
              title="gradient circle"
              width={320}
              height={320}
              className="object-contain absolute -bottom-6 -left-6 opacity-50"
              priority={false}
            />
            <Image
              src={`${env.NEXT_PUBLIC_CDN_URL}/images/shared/Gradient-sm.3JfJe8uG2DHb.png`}
              alt="gradient circle"
              title="gradient circle"
              width={260}
              height={260}
              className="object-contain absolute -top-6 -right-6 opacity-50"
              priority={false}
            />

            <div className="relative flex flex-col xl:flex-row items-center gap-10 xl:gap-10">
              <div className="w-full xl:w-1/2 text-center xl:text-left">
                <Typography variant="h2" className="text-white">
                  {title}
                </Typography>
                {subtitle && (
                  <Typography variant="large" className="text-gray-200 mt-4">
                    {subtitle}
                  </Typography>
                )}
                <div className="mt-6 flex justify-center xl:justify-start items-center xl:flex-row gap-4">
                  <GradientCardButtons buttons={buttons} buttonTrackingId={buttonTrackingId} />
                </div>
              </div>

              <div className="w-full xl:w-1/2">
                <div className="bg-white/10 backdrop-blur rounded-2xl p-4 xl:p-6">
                  <div className="flex items-center justify-between flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-white/10">
                    {(
                      stats || [
                        { value: "9,000+", label: "Happy Customers" },
                        { value: "150,000+", label: "Screens Managed" },
                        { value: "70+ ", label: "Countries" },
                      ]
                    ).map((s, i) => (
                      <div key={i} className="px-6 w-full  py-5 xl:px-8 xl:py-6 text-center">
                        <div className="text-white text-xl xl:text-3xl font-semibold tracking-tight text-center">
                          {s.value}
                        </div>
                        <div className="text-gray-300 mt-2 md:mt-3 text-sm xl:text-base text-center">
                          {s.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>
    );
  }

  if (isCtaBanner) {
    return (
      <Section variant="default" background="white">
        <div className="mx-auto">
          <div className="bg-navy rounded-4xl py-12 md:py-16 px-6 md:px-12 relative overflow-hidden">
            {/* Gradient background effects */}
             <Image
              src={`${env.NEXT_PUBLIC_CDN_URL}/images/shared/Gradient.DoxY_soZY97X.svg`}
              alt="gradient circle"
              title="gradient circle"
              width={280}
              height={280}
              className="object-contain absolute opacity-50 -bottom-0 -left-0"
              priority={false}

            />
            <Image
              src={`${env.NEXT_PUBLIC_CDN_URL}/images/shared/Gradient-sm.so5NaGNij4vP.svg`}
              alt="gradient circle"
              title="gradient circle"
              width={220}
              height={220}
              className="object-contain absolute opacity-50 -top-0 -right-0"
              priority={false}
            />

            <div
              className={`relative flex ${
                centered
                  ? "flex-col items-center justify-center gap-6 text-center"
                  : "flex-col lg:flex-row items-center justify-between gap-8"
              }`}
            >
              <div className={`${centered ? "text-center max-w-4xl mx-auto" : "text-center lg:text-left"} flex-1`}>
                <Typography
                  variant="h2"
                  className="text-white text-2xl lg:text-4xl font-semibold leading-tight"
                >
                  {title}
                </Typography>
                {subtitle && (
                  <Typography variant="body" className="text-gray-200 mt-4">
                    {subtitle}
                  </Typography>
                )}
              </div>

              <GradientCardButtons buttons={buttons} buttonTrackingId={buttonTrackingId} />
            </div>
          </div>
        </div>
      </Section>
    );
  }

  if (isOneRowCtaBanner) {
    return (
      <LazyBackground
        className={className}
        backgroundUrl={`${imgBaseUrl}/talk-sales-bg.mXlyIj_PoVHS.webp`}
      >
        <div className="text-center py-6 md:py-16 px-6 md:px-12">
          <Typography variant="h2" as="h2" className="font-medium mb-4">
            {title}
          </Typography>
          {/* optional subtitle */}
          {subtitle && (
            <Typography variant="large" as="p" className="text-secondary-text mb-4">
              {subtitle}
            </Typography>
          )}

          <div className="flex flex-col sm:flex-row gap-4 items-center justify-center max-w-md mx-auto">
            {buttons && <GradientCardButtons buttons={buttons} buttonTrackingId={buttonTrackingId} />}
          </div>
        </div>
      </LazyBackground>
    );
  }

  return (
    <Section variant="default" background="white">
      <LazyBackground
        className={className}
        backgroundUrl={`${imgBaseUrl}/talk-sales-bg.mXlyIj_PoVHS.webp`}
      >
        <div className="flex flex-col lg:flex-row justify-between items-center">
          <div className="flex flex-col items-center text-center lg:text-left lg:items-start w-full lg:w-7/12 p-8 gap-5">
            <Typography variant="h2" as="h2" className="text-[28px] font-medium">
              {title}
            </Typography>
            {/* optional subtitle */}
            {subtitle && (
              <Typography variant="large" as="p" className="text-secondary-text">
                {subtitle}
              </Typography>
            )}

            {buttons && <GradientCardButtons buttons={buttons} buttonTrackingId={buttonTrackingId} />}
          </div>

          <div className="w-full lg:w-5/12 h-full">
            {appImage ? (
              <Image
                src={`${env.NEXT_PUBLIC_CDN_URL}/images/apps/apps-with-pickcel.UHry12WEnu4q.svg`}
                alt="apps with pickcel"
                title="apps with pickcel"
                loading="lazy"
                decoding="async"
                width={500}
                height={120}
                className="w-[110%] h-full object-cover object-center"
                // style={{ width: "auto", height: "auto" }}
              />
            ) : (
              <Image
                src={`${imgBaseUrl}/engage_customers.0YRDp-4uFkNy.webp`}
                alt="customers engagement"
                title="customers engagement"
                loading="lazy"
                decoding="async"
                width={500}
                height={120}
                className="w-[110%] h-full object-cover object-center"
                // style={{ width: "auto", height: "auto" }}
              />
            )}
          </div>
        </div>
      </LazyBackground>
    </Section>
  );
};
